import { z } from 'zod'

export enum ChargeType {
  'Percentage' = 'Percentage',
  'Fixed Amount' = 'Fixed Amount',
}

export enum Quantifier {
  'Item Value' = 'Item Value',
  'Per Room Night' = 'Per Room Night',
  'Per Room Reservation' = 'Per Room Reservation',
  'Per Transaction' = 'Per Transaction',
  'Per Activity/Product Transaction' = 'Per Activity/Product Transaction',
  'Per Item' = 'Per Item',
  'Per Adult' = 'Per Adult',
  'Per Child' = 'Per Child',
  'Per Adult Per Night' = 'Per Adult Per Night',
  'Per Child Per Night' = 'Per Child Per Night',
  // Headcount (adults + children) quantifiers: 'Per Person' is the
  // activity/product scope, 'Per Person Per Night' the rate scope.
  'Per Person' = 'Per Person',
  'Per Person Per Night' = 'Per Person Per Night',
}

export enum TaxType {
  'Bed Tax' = 'Bed Tax',
  'City Fee' = 'City Fee',
  'City Tax' = 'City Tax',
  'County Tax' = 'County Tax',
  'Energy Tax' = 'Energy Tax',
  'Federal Tax' = 'Federal Tax',
  'Food & Beverage Tax' = 'Food & Beverage Tax',
  'Lodging Tax' = 'Lodging Tax',
  'Maintenance Fee' = 'Maintenance Fee',
  'Occupancy Tax' = 'Occupancy Tax',
  'Package Fee' = 'Package Fee',
  'Resort Fee' = 'Resort Fee',
  'Sales Tax' = 'Sales Tax',
  'Service Tax' = 'Service Tax',
  'State Tax' = 'State Tax',
  'Tourism Tax' = 'Tourism Tax',
  'Room Tax' = 'Room Tax',
  'Room Service Fee' = 'Room Service Fee',
  'Local Fee' = 'Local Fee',
  'Value Added Tax (VAT)' = 'Value Added Tax (VAT)',
  'Other Tax' = 'Other Tax',
  'Other Fee' = 'Other Fee',
}

export interface ExternalTax {
  Code: string
  Value: number
  Discriminator: ChargeType
}

export const taxAndFeesRulesFormSchema = z.object({
  itemName: z
    .string({ error: 'val.required' })
    .min(1, { error: 'val.required' }),
  chargeType: z.enum(ChargeType),
  quantifier: z.enum(Quantifier),
  type: z.enum(TaxType),
  value: z.transform(Number).pipe(z.number().min(0, { error: 'val.min1' })),
  externalIdentifier: z.string().optional().nullable(),
})

export type taxAndFeesRulesFormValues = z.infer<
  typeof taxAndFeesRulesFormSchema
>

export const taxAndFeesFormSchema = z.object({
  name: z.string({ error: 'val.required' }).min(1, { error: 'val.required' }),
  description: z.string(),
  rules: z.array(taxAndFeesRulesFormSchema),
})
export type TaxAndFeesFormValues = z.infer<typeof taxAndFeesFormSchema>
export type TaxAndFeesFormInputValues = z.input<typeof taxAndFeesFormSchema>

export const taxAndFeesPoliciesSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  name: z.string().min(1),
  description: z.string().min(1),
  rules: z.array(
    taxAndFeesRulesFormSchema.extend({
      id: z.string(),
      taxCancellationPolicyId: z.string(),
    })
  ),
})

export const taxAndFeesRulesSchema = taxAndFeesRulesFormSchema.extend({
  id: z.string(),
  taxCancellationPolicyId: z.string(),
})

export type TaxAndFeesPolicies = z.infer<typeof taxAndFeesPoliciesSchema>
export type TaxAndFeesRule = z.infer<typeof taxAndFeesRulesSchema>
